import {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import {
  HealthGraphContainer, HealthHistoryCardLoading, HealthHistoryContainer,
} from './styles';
import Skeleton from 'react-loading-skeleton';
import { HealthGraph } from './components/Graph';
import { HistoryHeader, HistoryTooltip, TitleGraph } from '../styles';
import { InfoTooltip } from '../components/InfoTooltip';
import { useTranslation } from 'react-i18next';
import { GetUnitHealthHistory } from '../types';
import { useFilterContext } from '../contexts/FilterContext';
import { useToast } from 'contexts/newToastContext';
import { getUnitHealthHistory } from '../helpers/overview';

interface HealthHistoryCardProps {
  hasHealth: boolean;
  loading: boolean;
}

export function HealthHistoryCard({
  hasHealth,
  loading,
}: Readonly<HealthHistoryCardProps>): ReactElement {
  const [graphData, setGraphData] = useState<GetUnitHealthHistory | undefined>();
  const [loadingGraph, setLoadingGraph] = useState(true);
  const { client, selectedOptions } = useFilterContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const getHealthHistory = useCallback(async () => {
    setLoadingGraph(true);
    const data = await getUnitHealthHistory(Number(client), {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
    });

    if (data) {
      const orderData = [...data.MONTHLY_HISTORY].reverse();
      setGraphData({
        MONTHLY_HISTORY: orderData,
      });
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosHistoricoSaude'), 'error');
    }
    setLoadingGraph(false);
  }, [client, selectedOptions, hasHealth, loading]);

  useEffect(() => {
    if (client && hasHealth && !loading) {
      getHealthHistory();
    }
  }, [client, hasHealth, loading, getHealthHistory]);

  useEffect(() => {
    if (!hasHealth || loading) {
      setGraphData(undefined);
      setLoadingGraph(false);
    }
  }, [hasHealth, loading]);

  if (loading || (loadingGraph && hasHealth)) {
    return (
      <HealthHistoryCardLoading>
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </HealthHistoryCardLoading>
    );
  }

  return (
    <HealthHistoryContainer
      data-testId="health-history-card"
    >
      <HistoryHeader>
        <TitleGraph>{t('historicoDeSaude')}</TitleGraph>
        <InfoTooltip
          id="health-history"
          disabled={!hasHealth}
        >
          <HistoryTooltip>
            <span>
              {t('mediaPorcentagemMesUnidadeStatusSaude')}
            </span>
          </HistoryTooltip>
        </InfoTooltip>
      </HistoryHeader>
      <HealthGraphContainer>
        <HealthGraph data={graphData} />
      </HealthGraphContainer>
    </HealthHistoryContainer>
  );
}
